import React, { useState } from 'react'
import {
  Box,
  Heading,
  Text,
  Badge,
  Button,
  Collapse,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Code,
} from '@chakra-ui/react'
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons'
import { PRM_CorrectnessTests, PRM_Error, PRM_CorrectnessTestCase } from '../types/unified_models'

interface CorrectnessTestModuleProps {
  correctness: PRM_CorrectnessTests | PRM_Error | null
}

// Type guard to check if correctness is a PRM_CorrectnessTests object
function isCorrectnessTests(correctness: PRM_CorrectnessTests | PRM_Error): correctness is PRM_CorrectnessTests {
  return !('error' in correctness) || !correctness.error
}

export default function CorrectnessTestModule({ correctness }: CorrectnessTestModuleProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const textColor = useColorModeValue('gray.800', 'gray.200')

  if (!correctness) {
    return (
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={CheckCircleIcon} color="gray.400" />
          <Heading size="sm" color={textColor}>Correctness Tests</Heading>
        </HStack>
        <Text fontSize="sm" color={textColor}>Correctness tests have not yet been run.</Text>
      </VStack>
    )
  }

  // Check if it's an error response
  if ('error' in correctness && correctness.error) {
    return (
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={WarningIcon} color="red.400" />
          <Heading size="sm" color={textColor}>Correctness Tests</Heading>
        </HStack>
        <Text fontWeight="bold" color="red.500" fontSize="sm">
          Failed to run / compile.
        </Text>
        <Button
          size="xs"
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Hide' : 'Show'} details
        </Button>
        <Collapse in={isExpanded}>
          <VStack align="stretch" spacing={2}>
            {correctness.stdout && (
              <Box>
                <Text fontWeight="medium" fontSize="sm">Standard Out:</Text>
                <Box
                  as="pre"
                  bg="gray.100"
                  p={2}
                  borderRadius="md"
                  fontSize="xs"
                  overflowX="auto"
                  maxH="200px"
                >
                  {correctness.stdout}
                </Box>
              </Box>
            )}
            {correctness.stderr && (
              <Box>
                <Text fontWeight="medium" fontSize="sm">Standard Error:</Text>
                <Box
                  as="pre"
                  bg="gray.100"
                  p={2}
                  borderRadius="md"
                  fontSize="xs"
                  overflowX="auto"
                  maxH="200px"
                >
                  {correctness.stderr}
                </Box>
              </Box>
            )}
          </VStack>
        </Collapse>
      </VStack>
    )
  }

  // At this point, correctness is guaranteed to be PRM_CorrectnessTests
  const correctnessTests = correctness as PRM_CorrectnessTests
  const isSuccess = correctnessTests.cases_failed === 0 && correctnessTests.checks_failed === 0

  return (
    <VStack align="stretch" spacing={3}>
      <HStack>
        <Icon 
          as={isSuccess ? CheckCircleIcon : WarningIcon} 
          color={isSuccess ? "green.400" : "red.400"} 
        />
        <Heading size="sm" color={textColor}>Correctness Tests</Heading>
      </HStack>
      
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <Text fontWeight="medium" fontSize="sm" color={textColor}>Test Results</Text>
          <Badge 
            colorScheme={isSuccess ? "green" : "red"}
            fontSize="xs"
          >
            {isSuccess ? "SUCCESS" : "FAILED"}
          </Badge>
        </HStack>

        <HStack justify="space-between" fontSize="xs">
          <Text color={textColor}>Test Cases:</Text>
          <Text color={textColor}>
            {correctnessTests.cases_passed} passed, {correctnessTests.cases_failed} failed
          </Text>
        </HStack>

        <HStack justify="space-between" fontSize="xs">
          <Text color={textColor}>Checks:</Text>
          <Text color={textColor}>
            {correctnessTests.checks_passed} passed, {correctnessTests.checks_failed} failed
          </Text>
        </HStack>

        {/* Test Cases Details - always visible */}
        {correctnessTests.test_cases && correctnessTests.test_cases.length > 0 && (
          <Box>
            <Text fontWeight="medium" mb={2} fontSize="sm" color={textColor}>Test Cases:</Text>
            <VStack align="stretch" spacing={2}>
              {correctnessTests.test_cases.map((testCase: PRM_CorrectnessTestCase, index: number) => (
                <Box key={index} p={2} bg="gray.100" borderRadius="md">
                  <HStack justify="space-between" mb={1}>
                    <Text fontWeight="medium" fontSize="xs" color="gray.800">
                      {testCase.name}
                    </Text>
                    <Badge 
                      colorScheme={testCase.success ? "green" : "red"}
                      size="sm"
                    >
                      {testCase.success ? "PASS" : "FAIL"}
                    </Badge>
                  </HStack>
                  {testCase.info && testCase.info.length > 0 && (
                    <VStack align="stretch" spacing={1}>
                      {testCase.info.map((info: string, infoIndex: number) => (
                        <Text key={infoIndex} fontSize="xs" color="gray.600">
                          {info}
                        </Text>
                      ))}
                    </VStack>
                  )}
                  {/* Expressions for failed cases (compact) */}
                  {!testCase.success && (testCase as any)?.expressions && (testCase as any).expressions.length > 0 && (
                    <VStack align="stretch" spacing={1} mt={2}>
                      <Text fontSize="xs" color="gray.500">Expression{(testCase as any).expressions.length > 1 ? 's' : ''}:</Text>
                      <Code fontSize="xs" p={1} whiteSpace="pre-wrap" wordBreak="break-word" colorScheme="red">
                        {(testCase as any).expressions[0]}
                      </Code>
                      {(testCase as any).expressions.length > 1 && (
                        <Text fontSize="xs" color="gray.500">
                          +{(testCase as any).expressions.length - 1} more
                        </Text>
                      )}
                    </VStack>
                  )}
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Logs (stdout/stderr) - toggle */}
        {(correctnessTests.stdout || correctnessTests.stderr) && (
          <>
            <Button
              size="xs"
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Hide logs' : 'Show logs'}
            </Button>
            <Collapse in={isExpanded}>
              <VStack align="stretch" spacing={3}>
                {correctnessTests.stdout && (
                  <Box>
                    <Text fontWeight="medium" fontSize="sm" color={textColor}>Standard Out:</Text>
                    <Box
                      as="pre"
                      bg="gray.100"
                      p={2}
                      borderRadius="md"
                      fontSize="xs"
                      overflowX="auto"
                      maxH="200px"
                    >
                      {correctnessTests.stdout}
                    </Box>
                  </Box>
                )}
                {correctnessTests.stderr && (
                  <Box>
                    <Text fontWeight="medium" fontSize="sm" color={textColor}>Standard Error:</Text>
                    <Box
                      as="pre"
                      bg="gray.100"
                      p={2}
                      borderRadius="md"
                      fontSize="xs"
                      overflowX="auto"
                      maxH="200px"
                    >
                      {correctnessTests.stderr}
                    </Box>
                  </Box>
                )}
              </VStack>
            </Collapse>
          </>
        )}
      </VStack>
    </VStack>
  )
} 